import React, { useEffect, useCallback, useState } from "react";
import EditMenu from "./EditMenu";
import CustomerToolbar from "./customers/components/CustomerToolbar";
import CustomerTable from "./customers/components/CustomerTable";
import LoadingState from "./customers/components/LoadingState";
import ErrorState from "./customers/components/ErrorState";
import { useCustomerData } from "./customers/hooks/useCustomerData";
import { usePagination } from "./customers/hooks/usePagination";
import { useCustomerDialogs } from "./customers/hooks/useCustomerDialogs";
import CustomerDialogs from "./customers/components/CustomerDialogs";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { postCustomer } from "src/shared/services/servicesv2";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/utils/useUserTheme";

const CustomerDataPage: React.FC = () => {
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, setPage } = usePagination(10);
  const { selectedCustomer, isEditOpen, isDeleteOpen, isAddOpen, openEdit, openDelete, openAdd, closeEdit, closeDelete, closeAdd, resetSelection } = useCustomerDialogs();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const { customerData, loading, error, refetch } = useCustomerData({showDeleted, searchTerm, page, rowsPerPage});

  const { sidebarThemeClass } = useUserTheme();

  useEffect(() => {
    document.title = pageTitle("Customers › Settings");
  }, []);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setPage(0);
    },
    [setPage]
  );

  const handleToggleDeleted = useCallback(() => {
    setShowDeleted((prev) => !prev);
    setSearchTerm((prev) => (prev ? "" : prev));
    setPage(0);
  }, [setPage]);

  const handleAddCustomer = useCallback(
    async (customerDataPayload: Partial<any>) => {
      try {
        const response = await postCustomer(customerDataPayload);
        if (response.data) {
          notifySuccess("Customer added successfully");
          closeAdd();
          setSearchTerm("");
          refetch();
        }
      } catch (err: any) {
        const message = err.response?.data?.message || (err.response?.status === 409 ? "Duplicate entry" : "Error adding customer");
        notifyError(message);
        console.error("Error adding customer:", err);
      }
    },
    [refetch, closeAdd]
  );

  const handleCustomerTablePageChange = (newPage: number) => {
    handleChangePage(null, newPage);
  };

  const handleCustomerTableRowsChange = (newRowsPerPage: number) => {
    handleChangeRowsPerPage({
      target: { value: newRowsPerPage.toString() },
    } as React.ChangeEvent<HTMLInputElement>);
    setPage(0);
  };

  return (
    <div className="p-1">
    <div className={`flex flex-col w-full shadow-inner rounded-lg h-[91vh] items-center ${sidebarThemeClass} overflow-hidden`}>
      <EditMenu editmenu={{ Customer: "customer" }} />

      <div className="customer-add-container">
        <CustomerToolbar
          searchTerm={searchTerm}
          showDeleted={showDeleted}
          onSearchChange={handleSearch}
          onToggleDeleted={handleToggleDeleted}
          onAddCustomer={openAdd}
        />
      </div>

      {loading && <LoadingState />}
      {error && <ErrorState message={error} />}

      {!loading && !error && (
        <CustomerTable
          data={customerData?.customer_details || []}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRecords={customerData?.totalRecords || 0}
          showDeleted={showDeleted}
          onEdit={openEdit}
          onDelete={(id) => {
            const customer = customerData?.customer_details.find((c) => c._id.$oid === id);
            if (customer) openDelete(customer);
          }}
          onPageChange={handleCustomerTablePageChange}
          onRowsPerPageChange={handleCustomerTableRowsChange}
        />
      )}

      <CustomerDialogs
        isEditOpen={isEditOpen}
        isDeleteOpen={isDeleteOpen}
        isAddOpen={isAddOpen}
        selectedCustomer={selectedCustomer}
        onCloseEdit={closeEdit}
        onCloseDelete={closeDelete}
        onCloseAdd={closeAdd}
        onAddCustomer={handleAddCustomer}
        onRefetch={refetch}
        onResetSelection={resetSelection}
      />
      {loading && <LoadingScreen />}
    </div>
    </div>
  );
};

export default CustomerDataPage;